import { Video, Sparkles, Lock, Eye } from "lucide-react";

import { LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeadline } from "@/components/ui/Headline";
import { InlineAlert } from "@/components/ui/States";
import { OrganizerShell } from "@/components/layout/OrganizerShell";

const STEPS = [
  {
    icon: Video,
    title: "Participants record pitches",
    desc: "Each person records a 60-second video and writes their goals. AI builds their profile instantly.",
  },
  {
    icon: Sparkles,
    title: "AI finds the best matches",
    desc: "The algorithm analyzes goals, complementary skills, and working styles to prioritize meaningful connections.",
  },
  {
    icon: Lock,
    title: "Contacts unlock before the event",
    desc: "Contact info appears only for mutual matches at a fixed reveal time — that creates anticipation and ensures attendance.",
  },
  {
    icon: Eye,
    title: "You stay in control",
    desc: "AI moderates every pitch automatically. You only review the flagged ones — typically 5 minutes for a 100-person event.",
  },
] as const;

export function HowItWorksPage() {
  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[720px] animate-fade-in">
        <PageHeadline
          eyebrow="Onboarding"
          title="How Finally works"
          subtitle="A simple process that creates meaningful connections"
        />

        <div className="mt-8 flex flex-col gap-3">
          {STEPS.map((s, i) => (
            <Card key={s.title} tone="default" padding="md">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#e6f1fb] text-[#2563eb]">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
                    Step {i + 1}
                  </p>
                  <h3 className="mt-0.5 text-[16px] font-bold text-[#1a1a1a]">{s.title}</h3>
                  <p className="mt-1 text-[14px] text-[#475569] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <InlineAlert tone="warning" className="mt-6">
          <strong className="block">Average organizer time: 15 minutes per event.</strong>
          <span className="text-[#92400e]">Average match rate: 42% of participants get at least one match.</span>
        </InlineAlert>

        <div className="mt-8">
          <LinkButton to="/events" fullWidth>
            Got it — set up my first event
          </LinkButton>
        </div>
      </div>
    </OrganizerShell>
  );
}
